 // @ts-ignore: No type declarations for @deck.gl/google-maps
import { GoogleMapsOverlay } from '@deck.gl/google-maps';
// @ts-ignore: No type declarations for @deck.gl/layers
import { IconLayer, ScatterplotLayer } from '@deck.gl/layers';
import { useMap } from '@vis.gl/react-google-maps';
import { useEffect, useMemo } from 'react';

export const GMapDeckOverlay = ({
    showPoi,
    showMbp,
    poi,
    mbp,
    onHoverPoi,
    handleClick,
    sub_district,
    focusedItem,
    brand,
}: {
    showPoi: boolean;
    showMbp: boolean | undefined;
    poi: any[];
    mbp: any[] | undefined;
    onHoverPoi?: (type: 'poi' | 'mbp', poi: any | null) => void;
    handleClick?: (e: any | null, brand: any | null) => void;
    brand: any,
    sub_district: any,
    focusedItem: {
        type: "poi" | "mbp" | null;
        data: any;
    }
}) => {
    const map = useMap();

    // Create overlay once
    const overlay = useMemo(
        () =>
            new GoogleMapsOverlay({
                interleaved: true,
            }),
        [map]
    );

    useEffect(() => {
        if (map && sub_district) overlay.setMap(map);
        else overlay.setMap(null);

        return () => {
            overlay.setProps({ layers: [] });
            overlay.setMap(null);
        };
    }, [map, overlay, sub_district]);

    useEffect(() => {
        const layers: any[] = [];

        if (showPoi && poi && poi?.length > 0) {
            // Visual POI layer
            layers.push(
                new ScatterplotLayer({
                    id: 'poi-layer-visual',
                    data: poi,
                    getPosition: (d: any) => [d.longitude, d.latitude],
                    radiusUnits: 'pixels',
                    getRadius: (d: any) => d.isSelected ? 7.5 : 5,

                    getFillColor: (d: any) => {
                        const isClicked = focusedItem?.type === 'poi' &&
                            focusedItem?.data?.latitude === d.latitude &&
                            focusedItem?.data?.longitude === d.longitude;
                        const isHovered = brand?.latitude === d.latitude &&
                            brand?.longitude === d.longitude;

                        if (isClicked) {
                            // Clicked state: #fff for normal POI, #A7BCFF for highlighted POI
                            return d.isSelected ? [167, 188, 255, 255] : [255, 255, 255, 255];
                        }

                        if (isHovered) {
                            // Hovered state: #fff for normal POI, #A7BCFF for highlighted POI
                            return d.isSelected ? [167, 188, 255, 255] : [255, 255, 255, 255];
                        }

                        return d.filtered_out ? [175, 175, 175, 1] : d.isSelected ? [0, 48, 199, 255] : [174, 194, 255, 255];
                    },
                    stroked: true,
                    lineWidthUnits: 'pixels',
                    getLineColor: (d: any) => {
                        const isClicked = focusedItem?.type === 'poi' &&
                            focusedItem?.data?.latitude === d.latitude &&
                            focusedItem?.data?.longitude === d.longitude;
                        const isHovered = brand?.latitude === d.latitude &&
                            brand?.longitude === d.longitude;

                        if (isClicked) {
                            // Clicked state: #07206A for normal POI, #0030C7 for highlighted POI
                            return d.isSelected ? [0, 48, 199, 255] : [7, 32, 106, 255];
                        }

                        if (isHovered) {
                            // Hovered state: #8DA9FF for normal POI, #E3EAFF for highlighted POI
                            return d.isSelected ? [227, 234, 255, 255] : [141, 169, 255, 255];
                        }

                        return d.filtered_out ? [69, 69, 69, 1] : d.isSelected ? [184, 201, 255, 255] : [7, 32, 106, 255];
                    },
                    getLineWidth: () => 1,
                    updateTriggers: {
                        getFillColor: [focusedItem, brand],
                        getLineColor: [focusedItem, brand],
                        getRadius: [focusedItem]
                    },
                    pickable: false,  // No interactions on visual layer
                })
            );

            // Interaction layer (transparent)
            layers.push(
                new ScatterplotLayer({
                    id: 'poi-layer-interaction',
                    data: poi,
                    getPosition: (d: any) => [d.longitude, d.latitude],
                    radiusUnits: 'pixels',
                    getRadius: () => 15,    // Large interaction radius
                    getFillColor: () => [0, 0, 0, 0],  // Fully transparent
                    stroked: false,
                    pickable: true,
                    onHover: (info: any) => {
                        const { object } = info;
                        if (object) onHoverPoi && !focusedItem.type && onHoverPoi('poi', object ?? null);
                        else onHoverPoi && onHoverPoi('poi', null);
                    },
                    onClick: (info: any) => {
                        const { object } = info;
                        handleClick && object && handleClick('poi', object ?? null);
                    }
                })
            );
        }

        if (showMbp && mbp && mbp?.length > 0) {
            layers.push(
                new IconLayer({
                    id: 'mbp-layer',
                    data: mbp,
                    getPosition: (d: any) => [d.longitude, d.latitude],
                    getIcon: (d: any) => ({
                        url: d.icon,
                        width: 100,
                        height: 100,
                        anchorY: 100,
                    }),
                    sizeUnits: 'pixels',
                    getSize: 27.17,
                    pickable: true,
                    onHover: (info: any) => {
                        const { object } = info;
                        onHoverPoi && object && !focusedItem.type && onHoverPoi('mbp', object ?? null);
                    },
                    onClick: (info: any) => {
                        const { object } = info;
                        handleClick && object && !focusedItem.type && handleClick('mbp', object ?? null);
                    }

                })
            );
        }

        overlay.setProps({ layers });
    }, [showPoi, showMbp, poi, mbp, onHoverPoi, overlay, map, focusedItem, brand]);

    return null;
}